<template>
  <div class="home">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>加载中...</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <p>加载失败：{{ error }}</p>
      <button @click="fetchData" class="retry-button">重试</button>
    </div>

    <!-- 内容区域 -->
    <div v-else>
      <!-- 直接遍历API返回的所有数据，按照原始顺序渲染 -->
      <div v-for="(item, index) in topics" :key="`topic-${item.id || index}`">
        <!-- 根据themeType值决定使用哪种布局 -->
        <div v-if="item.themeType === 'one'" class="first-box">
          <div v-for="(image, imgIndex) in getDisplayImages(item)" :key="`one-img-${image.id || imgIndex}`">
            <video
                v-if="isVideo(image.url)"
                :src="trimImageUrl(image.url)"
                autoplay muted loop playsinline
                class="responsive-image"
                @click="goToDetail(image.id)"
                style="cursor: pointer;"
            />
            <img
                v-else
                :src="trimImageUrl(image.url)"
                :alt="item.name || 'Single Image'"
                class="responsive-image"
                @click="goToDetail(image.id)"
                style="cursor: pointer;"
            >
          </div>
        </div>
        <div v-else-if="item.themeType === 'four'" class="second-box">
          <div v-for="(image, imgIndex) in getDisplayImages(item)" :key="`four-img-${image.id || imgIndex}`" class="grid-item">
            <video
                v-if="isVideo(image.url)"
                :src="trimImageUrl(image.url)"
                autoplay muted loop playsinline
                class="responsive-image"
                @click="goToDetail(image.id)"
                style="cursor: pointer;"
            />
            <img
                v-else
                :src="trimImageUrl(image.url)"
                :alt="item.name || 'Single Image'"
                class="responsive-image"
                @click="goToDetail(image.id)"
                style="cursor: pointer;"
            >
          </div>
        </div>
      </div>

      <!-- 无数据状态 -->
      <div v-if="topics.length === 0" class="empty-container">
        <p>暂无数据</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';  // 引入路由
import { useI18n } from 'vue-i18n';

// 定义图片数据类型
interface TopicImage {
  id: number;
  topicId: number;
  url: string;
  urlEn?: string | null;
}

// 定义主题数据类型
interface Topic {
  id: number;
  name: string;
  status: number;
  themeType: 'one' | 'four';
  url: string | null;
  urlEn?: string | null;
  createTime: string;
  images: TopicImage[];
  imagesEn?: TopicImage[];
}

// 定义API响应的数据类型
interface ApiResponse {
  code: number;
  result: Topic[];
  message: string;
}

export default defineComponent({
  setup() {
    const router = useRouter();  // 创建路由实例
    const { locale } = useI18n(); // 获取当前语言
    const topics = ref<Topic[]>([]);
    const loading = ref<boolean>(false);
    const error = ref<string>('');

    // 根据当前语言选择展示的图片数组
    const getDisplayImages = (item: Topic) => {
      return locale.value === 'en' && item.imagesEn?.length ? item.imagesEn : item.images;
    };

    // 获取数据的方法
    const fetchData = async () => {
      loading.value = true;
      error.value = '';
      
      try {
        // 调用真实接口
        // const response = await fetch('http://172.16.10.48:8080/jeecg-boot/drmotoev/topic/query-front');
        const response = await fetch('/jeecg-boot/drmotoev/topic/query-front');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data: ApiResponse = await response.json();
        
        // 检查响应代码
        if (data.code === 200) {
          topics.value = data.result || [];
        } else {
          throw new Error(data.message || '获取数据失败');
        }
      } catch (err) {
        error.value = err instanceof Error ? err.message : '未知错误';
        console.error('获取主题数据失败:', err);
        
        // 为了演示效果，使用模拟数据
        topics.value = [
          {
            id: 6,
            name: "小罗主题",
            status: 1,
            themeType: "one",
            url: null,
            createTime: "2025-10-11 09:33:12",
            images: [
              {
                id: 61,
                topicId: 6,
                url: " `https://newrockb2b.oss-cn-shenzhen.aliyuncs.com/0000/A86ARH_1774322565677.mp4` "
              }
            ],
            imagesEn: [
              {
                id: 1111,
                topicId: 6,
                url: " `https://newrockb2b.oss-cn-shenzhen.aliyuncs.com/images/1779341410013_gg_%E9%A0%81%E9%9D%A2_2.jpg` "
              }
            ]
          },
          {
            id: 7,
            name: "伦哥主题",
            status: 1,
            themeType: "four",
            url: null,
            createTime: "2025-10-11 09:57:59",
            images: [
              {
                id: 62,
                topicId: 7,
                url: " `https://newrockb2b.oss-cn-shenzhen.aliyuncs.com/0000/A86ARH_1774322565677.mp4` "
              },
              {
                id: 63,
                topicId: 7,
                url: " `https://newrockb2b.oss-cn-shenzhen.aliyuncs.com/0000/A86ARH_1774322565677.mp4` "
              },
              {
                id: 64,
                topicId: 7,
                url: " `https://newrockb2b.oss-cn-shenzhen.aliyuncs.com/0000/A86ARH_1774322565677.mp4` "
              },
              {
                id: 65,
                topicId: 7,
                url: " `https://newrockb2b.oss-cn-shenzhen.aliyuncs.com/0000/A86ARH_1774322565677.mp4` "
              }
            ],
            imagesEn: [
              {
                id: 62,
                topicId: 7,
                url: " `https://newrockb2b.oss-cn-shenzhen.aliyuncs.com/0000/A86ARH_1774322565677.mp4` "
              },
              {
                id: 63,
                topicId: 7,
                url: " `https://newrockb2b.oss-cn-shenzhen.aliyuncs.com/0000/A86ARH_1774322565677.mp4` "
              },
              {
                id: 64,
                topicId: 7,
                url: " `https://newrockb2b.oss-cn-shenzhen.aliyuncs.com/0000/A86ARH_1774322565677.mp4` "
              },
              {
                id: 65,
                topicId: 7,
                url: " `https://newrockb2b.oss-cn-shenzhen.aliyuncs.com/0000/A86ARH_1774322565677.mp4` "
              }
            ]
          }
        ];
      } finally {
        loading.value = false;
      }
    };

    // 处理图片URL中的额外字符
    const trimImageUrl = (url: string): string => {
      // 移除字符串两端的空格、反引号等特殊字符
      return url.replace(/^[\s`]+|[\s`]+$/g, '');
    };

    // 跳转到详情页的方法
    const goToDetail = (topicId: number) => {
      router.push({
        path: '/topic-detail',
        query: { topicId: topicId.toString() }
      });
    };

    const isVideo = (url:any) => {
      const videoExts = ['.mp4', '.webm', '.ogg', '.mov'];
      return videoExts.some(ext => url?.toLowerCase().includes(ext));
    };

    // 组件挂载时获取数据
    onMounted(() => {
      fetchData();
    });

    // 监听语言变化，重新获取数据
    watch(locale, () => {
      fetchData();
    });

    return {
      topics,
      loading,
      error,
      fetchData,
      trimImageUrl,
      goToDetail,
      isVideo,
      getDisplayImages
    };
  }
});
</script>

<style scoped>
.home {
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  padding-bottom: 40px; /* 为footer留出空间 */
}

/* 保持其他样式不变 */
.first-box {
  width: 100%;
  max-width: 2000px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 30px;
  margin-top: 80px;
  overflow: hidden; /* 防止内容溢出 */
}

/* second-box布局 - PC端 */
.second-box {
  width: 100%;
  max-width: 2000px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 20px;
  padding: 0 30px;
  margin-top: 40px;
  overflow: hidden; /* 防止内容溢出 */
}

.grid-item {
  width: 100%;
  height: 100%;
  min-height: 200px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}

/* 基础样式 - PC端 */
.responsive-image {
  width: 100%;
  max-width: 100%;
  height: 100%; /* 图片高度设置为100%以填充容器 */
  object-fit: contain; /* 保持图片比例，完全显示在容器内 */
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.responsive-image:hover {
  transform: scale(1.02);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

/* 加载状态样式 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  width: 100%;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #12aa4a;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 错误状态样式 */
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  width: 100%;
  color: #ff4d4f;
}

.retry-button {
  margin-top: 16px;
  padding: 6px 16px;
  background-color: #12aa4a;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.retry-button:hover {
  background-color: #0e8e3e;
}

/* 空数据状态样式 */
.empty-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  width: 100%;
  color: #999;
}

/* 平板端响应式调整 (769px - 1024px) */
@media (min-width: 769px) and (max-width: 1024px) {
  .first-box {
    padding: 0 20px;
    margin-top: 60px;
  }

  .second-box {
    padding: 0 20px;
    margin-top: 30px;
    gap: 15px;
  }

  .responsive-image {
    border-radius: 6px;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  }

  .responsive-image:hover {
    transform: scale(1.01);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  }
}

/* 手机端响应式调整 (≤768px) */
@media (max-width: 768px) {
  .first-box {
    padding: 0 15px;
    margin-top: 30px;
  }

  .second-box {
    padding: 0 15px;
    margin-top: 20px;
    gap: 10px;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(4, auto);
  }

  .grid-item {
    min-height: 150px;
  }

  .responsive-image {
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  /* 手机端移除hover效果，因为触摸设备不需要 */
  .responsive-image:hover {
    transform: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

.video-container {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}
iframe {
  width: 100%;
  height: 450px;
  border: none;
}
</style>
